import type { Item } from '../types';
import { bonusSources, itemDamageBonus } from './itemStats';

export type FormulaStats = {
  strength?: number;
  dexterity?: number;
  intelligence?: number;
  constitution?: number;
  level?: number;
  damage_formula?: string;
  basisschaden?: number;
  schaden?: number;
  items?: Array<{
    type?: string;
    name?: string;
    effect?: string;
    description?: string;
    strength_bonus?: number;
    damage_bonus?: number;
    health_bonus?: number;
  }>;
  effects?: Array<{
    type?: string;
    name?: string;
    effect?: string;
    description?: string;
    strength_bonus?: number;
    damage_bonus?: number;
  }>;
};

const STAT_NAMES = 'strength|dexterity|intelligence|constitution|level|staerke|stärke|geschick|intelligenz|konstitution|stufe|basisschaden|basis_schaden|base_damage|schaden';
const BASE_DAMAGE_NAME = /\b(basisschaden|basis_schaden|base_damage|schaden)\b/i;
const DEFAULT_DAMAGE_FORMULA = '5+strength*0.5';

function whole(value: number): number {
  return Math.max(0, Math.round(value));
}

function baseDamageOf(char: FormulaStats): number {
  if (typeof char.basisschaden === 'number' && Number.isFinite(char.basisschaden)) {
    return whole(char.basisschaden);
  }
  const formula = (char.damage_formula || DEFAULT_DAMAGE_FORMULA).trim();
  if (!formula || BASE_DAMAGE_NAME.test(formula)) return 0;
  const value = evaluateStatFormula(formula, {
    strength: char.strength,
    dexterity: char.dexterity,
    intelligence: char.intelligence,
    constitution: char.constitution,
    level: char.level,
  });
  return value == null ? 0 : whole(value);
}

function fullDamageOf(char: FormulaStats): number {
  if (typeof char.schaden === 'number' && Number.isFinite(char.schaden)) {
    return whole(char.schaden);
  }
  const bonus = itemDamageBonus(bonusSources({
    items: (char.items || []) as Item[],
    effects: (char.effects || []) as Item[],
  }));
  return whole(baseDamageOf(char) + bonus);
}

function statValue(char: FormulaStats, name: string): number {
  switch (name) {
    case 'strength':
    case 'staerke':
    case 'stärke':
      return char.strength || 10;
    case 'dexterity':
    case 'geschick':
      return char.dexterity || 10;
    case 'intelligence':
    case 'intelligenz':
      return char.intelligence || 10;
    case 'constitution':
    case 'konstitution':
      return char.constitution || 10;
    case 'level':
    case 'stufe':
      return char.level || 1;
    case 'basisschaden':
    case 'basis_schaden':
    case 'base_damage':
      return baseDamageOf(char);
    case 'schaden':
      return fullDamageOf(char);
    default:
      return 0;
  }
}

function normalizeFormula(formula: string): string {
  let text = formula.trim().replace(/[−–—]/g, '-');
  if (text.startsWith('{') && text.endsWith('}') && text.indexOf('{') === text.lastIndexOf('{')) {
    text = text.slice(1, -1).trim();
  }
  return text;
}

/** Wertet Charakterformeln aus. min/max begrenzen den Wert, unbekannte Zeichen ergeben null. */
export function evaluateStatFormula(formula: string | undefined, char: FormulaStats = {}): number | null {
  const text = normalizeFormula(String(formula ?? ''));
  if (!text) return null;
  try {
    const names = new RegExp(`\\b(${STAT_NAMES})\\b`, 'gi');
    let expression = text.toLowerCase().replace(names, (name) => String(statValue(char, name.toLowerCase())));
    expression = expression.replace(/\b(min|max)\b/g, (fn) => `Math.${fn}`);
    const withoutFunctions = expression.replace(/Math\.min/g, '').replace(/Math\.max/g, '');
    if (!/^[\d\s+\-*/().,]+$/.test(withoutFunctions)) return null;
    // eslint-disable-next-line no-new-func
    const result = Function(`"use strict"; return (${expression})`)();
    if (typeof result !== 'number' || !Number.isFinite(result)) return null;
    return Math.round(result * 10) / 10;
  } catch {
    return null;
  }
}
